import React, { useState, useEffect, useRef, useCallback } from 'react';
import { signOut, deleteUser, User } from 'firebase/auth';
import { auth } from '../firebaseClient';
import QRCodeStyling, { DotType, CornerSquareType, CornerDotType } from 'qr-code-styling';
import { toPng } from 'html-to-image';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import './HomePage.css';

const QR_PRESETS = [
  { name: '기본', dotType: 'square' as DotType, cornerSquareType: 'square' as CornerSquareType, cornerDotType: 'square' as CornerDotType, fgColor: '#000000', bgColor: '#ffffff', useGradient: false, gradientColor: '#000000' },
  { name: '모던', dotType: 'rounded' as DotType, cornerSquareType: 'extra-rounded' as CornerSquareType, cornerDotType: 'dot' as CornerDotType, fgColor: '#1a1a2e', bgColor: '#ffffff', useGradient: false, gradientColor: '#1a1a2e' },
  { name: '블루', dotType: 'rounded' as DotType, cornerSquareType: 'extra-rounded' as CornerSquareType, cornerDotType: 'dot' as CornerDotType, fgColor: '#0061ff', bgColor: '#ffffff', useGradient: true, gradientColor: '#60efff' },
  { name: '퍼플', dotType: 'dots' as DotType, cornerSquareType: 'dot' as CornerSquareType, cornerDotType: 'dot' as CornerDotType, fgColor: '#7b2ff7', bgColor: '#ffffff', useGradient: true, gradientColor: '#c471f5' },
  { name: '선셋', dotType: 'extra-rounded' as DotType, cornerSquareType: 'extra-rounded' as CornerSquareType, cornerDotType: 'dot' as CornerDotType, fgColor: '#ff6b6b', bgColor: '#ffffff', useGradient: true, gradientColor: '#ffd93d' },
  { name: '다크', dotType: 'rounded' as DotType, cornerSquareType: 'extra-rounded' as CornerSquareType, cornerDotType: 'dot' as CornerDotType, fgColor: '#e0e0e0', bgColor: '#1a1a2e', useGradient: false, gradientColor: '#e0e0e0' },
];

const TEMPLATES = [
  { id: 'web-payment', name: '모던 카드' },
  { id: 'web-payment-simple', name: '심플 화이트' },
  { id: 'web-payment-dark', name: '프리미엄 다크' },
];

const PRINT_SIZES = [
  { id: 'a4', name: 'A4' },
  { id: 'card', name: '명함' },
  { id: 'sticker', name: '스티커' },
];

const BUSINESS_TYPES = [
  { id: 'cafe', name: '☕ 카페/음료', images: ['/backgrounds/cafe_01.jpg'] },
  { id: 'restaurant', name: '🍽️ 음식점', images: ['/backgrounds/restaurant_01.jpg'] },
  { id: 'delivery', name: '🍕 배달/패스트푸드', images: ['/backgrounds/delivery_01.jpg'] },
  { id: 'beauty', name: '💆 뷰티/헤어', images: ['/backgrounds/beauty_01.jpg'] },
  { id: 'retail', name: '🛍️ 소매/쇼핑', images: ['/backgrounds/retail_01.jpg'] },
  { id: 'accommodation', name: '🏨 숙박', images: ['/backgrounds/accommodation_01.jpg'] },
  { id: 'pet', name: '🐾 반려동물', images: ['/backgrounds/pet_01.jpg'] },
  { id: 'other', name: '🎨 기타 서비스', images: ['/backgrounds/other_01.jpg'] },
  { id: 'season', name: '🎉 시즌/이벤트', images: ['/backgrounds/season_01.jpg'] },
];

const STORAGE_KEY = 'sme_qr_accounts';

interface Account {
  id: string;
  bankName: string;
  accountNumber: string;
  accountHolder: string;
  nickname: string;
  backgroundImage: string;
}

export default function HomePage({ user }: { user: User }) {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [newAccount, setNewAccount] = useState({ bankName: '', accountNumber: '', accountHolder: '', nickname: '', backgroundImage: '' });
  const [accountLoading, setAccountLoading] = useState(false);
  const [selectedBusinessType, setSelectedBusinessType] = useState('');

  const [template, setTemplate] = useState('web-payment');
  const [selectedPreset, setSelectedPreset] = useState('기본');
  const [qrSize, setQrSize] = useState(256);
  const [finalQrValue, setFinalQrValue] = useState('');
  const [qrFgColor, setQrFgColor] = useState('#000000');
  const [qrBgColor, setQrBgColor] = useState('#ffffff');
  const [dotType, setDotType] = useState<DotType>('square');
  const [cornerSquareType, setCornerSquareType] = useState<CornerSquareType>('square');
  const [cornerDotType, setCornerDotType] = useState<CornerDotType>('square');
  const [useGradient, setUseGradient] = useState(false);
  const [gradientColor, setGradientColor] = useState('#0000ff');

  const [useAmount, setUseAmount] = useState(false);
  const [unitPrice, setUnitPrice] = useState('');

  const [printSize, setPrintSize] = useState('a4');
  const [showPrintPreview, setShowPrintPreview] = useState(false);

  const [popup, setPopup] = useState({ show: false, msg: '' });
  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(false);

  const showPopup = (msg: string) => setPopup({ show: true, msg });
  const hidePopup = () => setPopup({ show: false, msg: '' });

  const qrRef = useRef<HTMLDivElement>(null);
  const qrCanvasRef = useRef<HTMLDivElement>(null);
  const printQrCanvasRef = useRef<HTMLDivElement>(null);
  const printPreviewRef = useRef<HTMLDivElement>(null);
  const qrCodeInstance = useRef<QRCodeStyling | null>(null);
  const printQrInstance = useRef<QRCodeStyling | null>(null);
  const previewRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  useEffect(() => {
    fetchAccounts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAccounts = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const data: Account[] = stored ? JSON.parse(stored) : [];
      setAccounts(data);
      if (data.length > 0 && !selectedAccount) setSelectedAccount(data[0]);
    } catch (err) { console.error(err); }
  };

  const handleAddAccount = () => {
    if (!newAccount.bankName || !newAccount.accountNumber || !newAccount.accountHolder) return;
    setAccountLoading(true);
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const existing: Account[] = stored ? JSON.parse(stored) : [];
      const newItem: Account = { ...newAccount, id: Date.now().toString() };
      const updated = [...existing, newItem];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      setNewAccount({ bankName: '', accountNumber: '', accountHolder: '', nickname: '', backgroundImage: '' });
      setSelectedBusinessType('');
      setShowAddAccount(false);
      fetchAccounts();
    } catch (err) { console.error(err); }
    setAccountLoading(false);
  };

  const handleDeleteAccount = (id: string) => {
    if (!window.confirm('이 계좌를 삭제하시겠습니까?')) return;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const existing: Account[] = stored ? JSON.parse(stored) : [];
      const updated = existing.filter(acc => acc.id !== id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      if (selectedAccount?.id === id) setSelectedAccount(updated[0] || null);
      fetchAccounts();
    } catch (err) { console.error(err); }
  };

  // ✅ 회원탈퇴 - 재인증 필요 시 로그아웃 안내
  const handleWithdraw = async () => {
    setShowWithdrawConfirm(false);
    try {
      localStorage.removeItem(STORAGE_KEY);
      await deleteUser(user);
    } catch (err: any) {
      if (err.code === 'auth/requires-recent-login') {
        showPopup('보안을 위해 로그아웃 후 다시 로그인하여 탈퇴해 주세요.');
        setTimeout(() => {
          hidePopup();
          signOut(auth);
        }, 3000);
      } else {
        showPopup('❌ 탈퇴에 실패했습니다. 다시 시도해주세요.');
        setTimeout(() => hidePopup(), 3000);
      }
    }
  };

  const handleBusinessTypeChange = (typeId: string) => {
    setSelectedBusinessType(typeId);
    setNewAccount({ ...newAccount, backgroundImage: '' });
  };

  const selectedTypeImages = BUSINESS_TYPES.find(bt => bt.id === selectedBusinessType)?.images || [];

  useEffect(() => {
    QR_PRESETS.forEach((preset) => {
      const container = previewRefs.current[preset.name];
      if (!container || container.childNodes.length > 0) return;
      const dotsOptions: any = { type: preset.dotType };
      if (preset.useGradient) {
        dotsOptions.gradient = { type: 'linear', rotation: Math.PI / 4, colorStops: [{ offset: 0, color: preset.fgColor }, { offset: 1, color: preset.gradientColor }] };
      } else { dotsOptions.color = preset.fgColor; }
      const qr = new QRCodeStyling({ width: 60, height: 60, data: 'https://example.com', dotsOptions, backgroundOptions: { color: preset.bgColor }, cornersSquareOptions: { type: preset.cornerSquareType, color: preset.fgColor }, cornersDotOptions: { type: preset.cornerDotType, color: preset.fgColor }, qrOptions: { errorCorrectionLevel: 'M' } });
      qr.append(container);
    });
  }, []);

  const applyPreset = (preset: typeof QR_PRESETS[0]) => {
    setSelectedPreset(preset.name);
    setQrFgColor(preset.fgColor);
    setQrBgColor(preset.bgColor);
    setDotType(preset.dotType);
    setCornerSquareType(preset.cornerSquareType);
    setCornerDotType(preset.cornerDotType);
    setUseGradient(preset.useGradient);
    setGradientColor(preset.gradientColor);
    if (finalQrValue && qrCodeInstance.current) {
      const dotsOptions: any = { type: preset.dotType };
      if (preset.useGradient) { dotsOptions.gradient = { type: 'linear', rotation: Math.PI / 4, colorStops: [{ offset: 0, color: preset.fgColor }, { offset: 1, color: preset.gradientColor }] }; }
      else { dotsOptions.color = preset.fgColor; }
      qrCodeInstance.current.update({ width: qrSize, height: qrSize, data: finalQrValue, dotsOptions, backgroundOptions: { color: preset.bgColor }, cornersSquareOptions: { type: preset.cornerSquareType, color: preset.fgColor }, cornersDotOptions: { type: preset.cornerDotType, color: preset.fgColor } });
    }
  };

  const getQrOptions = useCallback(() => {
    const dotsOptions: any = { type: dotType };
    if (useGradient) { dotsOptions.gradient = { type: 'linear', rotation: Math.PI / 4, colorStops: [{ offset: 0, color: qrFgColor }, { offset: 1, color: gradientColor }] }; }
    else { dotsOptions.color = qrFgColor; }
    return { width: qrSize, height: qrSize, data: finalQrValue || 'https://example.com', dotsOptions, backgroundOptions: { color: qrBgColor }, cornersSquareOptions: { type: cornerSquareType, color: qrFgColor }, cornersDotOptions: { type: cornerDotType, color: qrFgColor }, qrOptions: { errorCorrectionLevel: 'M' as const } };
  }, [finalQrValue, qrFgColor, qrBgColor, qrSize, dotType, cornerSquareType, cornerDotType, useGradient, gradientColor]);

  useEffect(() => {
    if (!finalQrValue || !qrCanvasRef.current) return;
    const options = getQrOptions();
    if (!qrCodeInstance.current) {
      qrCodeInstance.current = new QRCodeStyling(options);
      qrCanvasRef.current.innerHTML = '';
      qrCodeInstance.current.append(qrCanvasRef.current);
    } else { qrCodeInstance.current.update(options); }
  }, [finalQrValue, getQrOptions]);

  useEffect(() => {
    if (!finalQrValue || !printQrCanvasRef.current || !showPrintPreview) return;
    const printQrSize = printSize === 'card' ? 125 : printSize === 'sticker' ? 170 : 180;
    const dotsOptions: any = { type: dotType };
    if (useGradient) { dotsOptions.gradient = { type: 'linear', rotation: Math.PI / 4, colorStops: [{ offset: 0, color: qrFgColor }, { offset: 1, color: gradientColor }] }; }
    else { dotsOptions.color = qrFgColor; }
    printQrCanvasRef.current.innerHTML = '';
    printQrInstance.current = new QRCodeStyling({
      width: printQrSize, height: printQrSize, data: finalQrValue,
      dotsOptions, backgroundOptions: { color: qrBgColor },
      cornersSquareOptions: { type: cornerSquareType, color: qrFgColor },
      cornersDotOptions: { type: cornerDotType, color: qrFgColor },
      qrOptions: { errorCorrectionLevel: 'M' }
    });
    printQrInstance.current.append(printQrCanvasRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalQrValue, showPrintPreview, printSize]);

  const handleGenerate = () => {
    if (!selectedAccount) return;
    qrCodeInstance.current = null;
    if (qrCanvasRef.current) qrCanvasRef.current.innerHTML = '';
    const baseUrl = 'https://sme-qr-pay.pages.dev';
    const params = new URLSearchParams();
    params.set('type', 'payment');
    params.set('template', template);
    params.set('bank', selectedAccount.bankName);
    params.set('accountNumber', selectedAccount.accountNumber);
    params.set('accountHolder', selectedAccount.accountHolder);
    params.set('nickname', selectedAccount.nickname || selectedAccount.bankName);
    if (useAmount && unitPrice) params.set('unitPrice', unitPrice);
    if (selectedAccount.backgroundImage) params.set('bg', selectedAccount.backgroundImage);
    setFinalQrValue(`${baseUrl}/display?${params.toString()}`);
    setShowPrintPreview(false);
  };

  const handleDownload = async () => {
    if (!qrCodeInstance.current) return;
    showPopup('저장 중...');
    try {
      const blob = await qrCodeInstance.current.getRawData('png');
      if (!blob) return;
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = (reader.result as string).split(',')[1];
        const fileName = `계좌QR_${selectedAccount?.bankName}_${Date.now()}.png`;
        await Filesystem.writeFile({
          path: fileName,
          data: base64Data,
          directory: Directory.Documents,
        });
        showPopup('✅ QR이 갤러리에 저장됐습니다!');
        setTimeout(() => hidePopup(), 2000);
      };
      reader.readAsDataURL(blob as Blob);
    } catch (err) {
      console.error(err);
      showPopup('❌ 저장에 실패했습니다.');
      setTimeout(() => hidePopup(), 2000);
    }
  };

  const handlePrintDownload = async () => {
    if (!printPreviewRef.current) return;
    showPopup('저장 중...');
    try {
      const dataUrl = await toPng(printPreviewRef.current, { cacheBust: true, pixelRatio: 1 });
      const base64Data = dataUrl.split(',')[1];
      const sizeName = PRINT_SIZES.find(s => s.id === printSize)?.name || '';
      const fileName = `계좌QR_${sizeName}_${selectedAccount?.bankName}_${Date.now()}.png`;
      await Filesystem.writeFile({
        path: fileName,
        data: base64Data,
        directory: Directory.Documents,
      });
      showPopup('✅ 갤러리에 저장됐습니다!');
      setTimeout(() => hidePopup(), 2000);
    } catch (err) {
      console.error(err);
      showPopup('❌ 저장에 실패했습니다.');
      setTimeout(() => hidePopup(), 2000);
    }
  };

  const handlePrint = async () => {
    if (!selectedAccount || !finalQrValue) return;
    if (!printPreviewRef.current) return;
    try {
      const dataUrl = await toPng(printPreviewRef.current, { cacheBust: true, pixelRatio: 3 });
      const base64Data = dataUrl.split(',')[1];
      const fileName = `계좌QR_인쇄용_${Date.now()}.png`;
      const savedFile = await Filesystem.writeFile({
        path: fileName,
        data: base64Data,
        directory: Directory.Cache,
      });
      await Share.share({
        title: '계좌QR 인쇄',
        text: `${selectedAccount.nickname || selectedAccount.bankName} 계좌QR 인쇄`,
        url: savedFile.uri,
        dialogTitle: '인쇄 또는 공유하기',
      });
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="home-container">

      {/* 저장 팝업 */}
      {popup.show && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 9999
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: '12px', padding: '28px 32px',
            textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            fontSize: '1rem', fontWeight: '600', color: '#333'
          }}>
            {popup.msg}
          </div>
        </div>
      )}

      {/* 회원탈퇴 확인 팝업 */}
      {showWithdrawConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 9999
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: '12px', padding: '28px 24px',
            width: '80%', maxWidth: '320px', textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
          }}>
            <p style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '8px' }}>회원탈퇴</p>
            <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '24px' }}>
              탈퇴 시 모든 데이터가 삭제됩니다.<br />정말 탈퇴하시겠습니까?
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowWithdrawConfirm(false)}
                style={{
                  flex: 1, padding: '12px', borderRadius: '8px',
                  border: '1px solid #ddd', backgroundColor: '#f5f5f5',
                  fontSize: '0.95rem', cursor: 'pointer'
                }}
              >취소</button>
              <button
                onClick={handleWithdraw}
                style={{
                  flex: 1, padding: '12px', borderRadius: '8px',
                  border: 'none', backgroundColor: '#ff4444',
                  color: '#fff', fontSize: '0.95rem', cursor: 'pointer'
                }}
              >탈퇴</button>
            </div>
          </div>
        </div>
      )}

      <header className="home-header">
        <div className="home-header-left">
          <span className="home-logo">💳</span>
          <span className="home-brand">소상공인 계좌QR</span>
        </div>
        <div className="home-header-right">
          <span className="home-user">{user.email}</span>
          <button className="home-logout-btn" onClick={() => signOut(auth)}>로그아웃</button>
          <button className="home-withdraw-btn" onClick={() => setShowWithdrawConfirm(true)}>회원탈퇴</button>
        </div>
      </header>

      <div className="home-content">
        <div className="home-left">
          <div className="section-card">
            <div className="section-header">
              <h3>💳 내 계좌 관리</h3>
              <button className="btn-add" onClick={() => {
                setShowAddAccount(!showAddAccount);
                setSelectedBusinessType('');
                setNewAccount({ bankName: '', accountNumber: '', accountHolder: '', nickname: '', backgroundImage: '' });
              }}>
                {showAddAccount ? '취소' : '+ 계좌 추가'}
              </button>
            </div>
            {showAddAccount && (
              <div className="add-account-form">
                <input placeholder="계좌상호(별명) 예: 몽키버블티 군포점" value={newAccount.nickname} onChange={e => setNewAccount({ ...newAccount, nickname: e.target.value })} />
                <input placeholder="은행명 (예: 신한은행)" value={newAccount.bankName} onChange={e => setNewAccount({ ...newAccount, bankName: e.target.value })} />
                <input placeholder="계좌번호" value={newAccount.accountNumber} onChange={e => setNewAccount({ ...newAccount, accountNumber: e.target.value })} />
                <input placeholder="예금주" value={newAccount.accountHolder} onChange={e => setNewAccount({ ...newAccount, accountHolder: e.target.value })} />
                <div className="business-type-selector">
                  <p className="business-type-label">🏢 업종 선택 (선택사항)</p>
                  <div className="business-type-buttons">
                    {BUSINESS_TYPES.map(bt => (
                      <button key={bt.id} type="button" className={`business-type-btn ${selectedBusinessType === bt.id ? 'selected' : ''}`} onClick={() => handleBusinessTypeChange(bt.id)}>
                        {bt.name}
                      </button>
                    ))}
                  </div>
                  {selectedBusinessType && (
                    <div className="business-image-grid">
                      {selectedTypeImages.map((img, idx) => (
                        <div key={idx} className={`business-image-item ${newAccount.backgroundImage === img ? 'selected' : ''}`} onClick={() => setNewAccount({ ...newAccount, backgroundImage: newAccount.backgroundImage === img ? '' : img })}>
                          <img src={img} alt={`배경 ${idx + 1}`} className="business-thumb" />
                          {newAccount.backgroundImage === img && <div className="business-image-check">✅</div>}
                        </div>
                      ))}
                    </div>
                  )}
                  {newAccount.backgroundImage && <p className="business-selected-msg">✅ 배경 이미지 선택 완료</p>}
                </div>
                <button className="btn-save" onClick={handleAddAccount} disabled={accountLoading}>
                  {accountLoading ? '저장 중...' : '저장'}
                </button>
              </div>
            )}
            <div className="account-list">
              {accounts.length === 0 ? (
                <p className="no-account">등록된 계좌가 없습니다. 계좌를 추가해주세요!</p>
              ) : (
                accounts.map(acc => (
                  <div key={acc.id} className={`account-item ${selectedAccount?.id === acc.id ? 'selected' : ''}`} onClick={() => setSelectedAccount(acc)}>
                    <div className="account-info">
                      <span className="account-nickname">{acc.nickname || acc.bankName}</span>
                      <span className="account-detail">{acc.bankName} {acc.accountNumber}</span>
                      <span className="account-holder">{acc.accountHolder}</span>
                    </div>
                    <button className="btn-delete" onClick={e => { e.stopPropagation(); handleDeleteAccount(acc.id); }}>삭제</button>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="section-card">
            <h3>⚙️ QR 설정</h3>
            <div className="setting-group">
              <label className="setting-label">송금 페이지 템플릿</label>
              <div className="template-options">
                {TEMPLATES.map(t => (
                  <label key={t.id} className={`template-option ${template === t.id ? 'selected' : ''}`}>
                    <input type="radio" name="template" value={t.id} checked={template === t.id} onChange={e => setTemplate(e.target.value)} />
                    {t.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="setting-group">
              <label className="setting-label">
                <input type="checkbox" checked={useAmount} onChange={e => setUseAmount(e.target.checked)} />
                &nbsp; 금액 계산기 사용
              </label>
              <p className="setting-hint">균일가일 때만 사용 (예: 뷔페, 한식뷔페)</p>
              {useAmount && (
                <input className="setting-input" type="number" placeholder="단가 입력 (예: 5700)" value={unitPrice} onChange={e => setUnitPrice(e.target.value)} />
              )}
            </div>
            <div className="setting-group">
              <label className="setting-label" style={{ color: '#999' }}>
                ⏰ QR 만료시간 설정 (추가 업데이트 예정)
              </label>
            </div>
            <div className="setting-group">
              <label className="setting-label">QR 디자인 프리셋</label>
              <div className="preset-grid">
                {QR_PRESETS.map(preset => (
                  <div key={preset.name} className={`preset-card ${selectedPreset === preset.name ? 'preset-active' : ''}`} onClick={() => applyPreset(preset)}>
                    <div className="preset-preview" style={{ backgroundColor: preset.bgColor }} ref={el => { previewRefs.current[preset.name] = el; }}></div>
                    <span className="preset-name">{preset.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <button className="btn-generate" onClick={handleGenerate} disabled={!selectedAccount}>
            {selectedAccount ? '🔲 QR 코드 생성하기' : '계좌를 먼저 선택해주세요'}
          </button>
        </div>

        <div className="home-right">
          <div className="qr-preview-card">
            <h3>생성된 QR 코드</h3>
            {finalQrValue ? (
              <>
                {selectedAccount && (
                  <div className="qr-account-info">
                    <span>{selectedAccount.bankName}</span>
                    <span>{selectedAccount.accountNumber}</span>
                    <span>{selectedAccount.accountHolder}</span>
                  </div>
                )}
                <div ref={qrRef} className="qr-wrapper" style={{ backgroundColor: qrBgColor }}>
                  <div ref={qrCanvasRef}></div>
                </div>
                <div className="qr-size-control">
                  <label className="qr-size-label">QR 크기 조절</label>
                  <select className="setting-select" value={qrSize} onChange={e => setQrSize(Number(e.target.value))}>
                    <option value={192}>중간 (192)</option>
                    <option value={256}>기본 (256)</option>
                    <option value={320}>크게 (320)</option>
                    <option value={400}>매우 크게 (400)</option>
                  </select>
                </div>
                <button className="btn-download" onClick={handleDownload}>⬇️ QR 다운로드</button>
                <div className="qr-url">
                  <p>QR 링크:</p>
                  <a href={finalQrValue} target="_blank" rel="noopener noreferrer">미리보기 →</a>
                </div>
              </>
            ) : (
              <div className="qr-placeholder">
                <div className="qr-placeholder-icon">🔲</div>
                <p>계좌 선택 후<br />QR 코드 생성하기 버튼을<br />눌러주세요</p>
              </div>
            )}
          </div>

          {finalQrValue && (
            <div className="print-preview-card">
              <div className="print-preview-header">
                <h3>🖨️ 프린트 미리보기</h3>
                <button className="btn-print-toggle" onClick={() => setShowPrintPreview(!showPrintPreview)}>
                  {showPrintPreview ? '닫기' : '열기'}
                </button>
              </div>
              {showPrintPreview && (
                <>
                  <div className="print-size-selector">
                    {PRINT_SIZES.map(size => (
                      <button key={size.id} className={`print-size-btn ${printSize === size.id ? 'active' : ''}`} onClick={() => setPrintSize(size.id)}>
                        {size.name}
                      </button>
                    ))}
                  </div>
                  <div className="print-preview-wrapper">
                    <div ref={printPreviewRef} className={`print-template print-${printSize}`}>
                      {printSize === 'a4' && (
                        <>
                          <div className="print-a4-header">
                            <div className="print-a4-deco left"></div>
                            <div className="print-a4-title-area">
                              <p className="print-a4-label">— 계좌이체 —</p>
                              <p className="print-a4-shop">{selectedAccount?.nickname || selectedAccount?.bankName}</p>
                              <p className="print-a4-bank">{selectedAccount?.bankName}</p>
                              <p className="print-a4-account">{selectedAccount?.accountNumber}</p>
                              <p className="print-a4-holder">예금주 : {selectedAccount?.accountHolder}</p>
                            </div>
                            <div className="print-a4-deco right"></div>
                          </div>
                          <div className="print-a4-scan">SCAN HERE</div>
                          <div className="print-a4-qr-wrapper">
                            <div ref={printQrCanvasRef}></div>
                          </div>
                        </>
                      )}
                      {printSize === 'card' && (
                        <div className="print-card-layout">
                          <div className="print-card-top">
                            <p className="print-card-label">계좌이체</p>
                            <p className="print-card-shop">{selectedAccount?.nickname || selectedAccount?.bankName}</p>
                            <p className="print-card-bank">{selectedAccount?.bankName}</p>
                            <p className="print-card-account">{selectedAccount?.accountNumber}</p>
                            <p className="print-card-holder">예금주 : {selectedAccount?.accountHolder}</p>
                          </div>
                          <div className="print-card-bottom">
                            <div ref={printQrCanvasRef}></div>
                          </div>
                        </div>
                      )}
                      {printSize === 'sticker' && (
                        <>
                          <p className="print-sticker-shop">{selectedAccount?.nickname || selectedAccount?.bankName}</p>
                          <div className="print-sticker-qr">
                            <div ref={printQrCanvasRef}></div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="print-action-btns">
                    <button className="btn-print-download" onClick={handlePrintDownload}>⬇️ 이미지 저장</button>
                    <button className="btn-print-print" onClick={handlePrint}>🖨️ 인쇄/공유하기</button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
